/**
 * What each dropped document is, from the summary the simulator writes once it is read (handoff 4,
 * Sim-Start): a short chip, a label, and what is still missing for the order chosen.
 */

export type SimKind = "oio" | "oia"
export type DocKind = "scn" | "drc" | "reply" | "hearing" | "oio" | "oia" | "apl" | "other"

const RULES: [DocKind, RegExp][] = [
  ["drc", /drc[\s-]?0?1a?\b|summary of (the )?(show cause )?notice/i],
  ["reply", /\breply\b|\bresponse\b|drc[\s-]?0?6/i],
  ["hearing", /hearing|personal hearing|\bph\b/i],
  ["apl", /apl[\s-]?0?1|memorandum of appeal|grounds of appeal|\bappeal\b(?! order)/i],
  ["oia", /order[\s-]in[\s-]appeal|\boia\b|appellate (authority|order)/i],
  ["oio", /order[\s-]in[\s-]original|\boio\b|drc[\s-]?0?7|adjudication order|\border\b/i],
  ["scn", /show cause|\bscn\b|notice/i],
]

export function docKind(summary: Record<string, unknown> | null | undefined, filename = ""): DocKind {
  const text = `${String(summary?.doc_type ?? "")} ${String(summary?.title ?? "")}`.trim() || filename
  for (const [kind, rule] of RULES) if (rule.test(text)) return kind
  return "other"
}

/** One part of a file: the kind of a run of its pages. `from`/`to` are 1-based pages, absent when the file is one kind throughout. */
export interface DocPart { kind: DocKind; from?: number; to?: number }

const PART_KIND: Record<string, DocKind> = { drc01: "drc", scn: "scn", reply: "reply", order: "oio", hearing: "hearing" }

/**
 * What a file holds. The server reads each page and records the runs of one kind on the summary
 * (`parts`), so one PDF can be a DRC-01 summary and the notice behind it. Without parts (older files,
 * a failed read) the file is the one kind its summary or name says.
 */
export function docParts(summary: Record<string, unknown> | null | undefined, filename = "", sim: SimKind = "oio"): DocPart[] {
  const raw = Array.isArray(summary?.parts) ? (summary?.parts as Record<string, unknown>[]) : []
  const parts: DocPart[] = []
  for (const p of raw) {
    const kind = PART_KIND[String(p?.kind)]
    if (!kind || typeof p.page_from !== "number" || typeof p.page_to !== "number") continue
    // An order in a bundle is the one under appeal for an appeal; an OIO draft has no use for it.
    parts.push({ kind: kind === "oio" && sim !== "oia" ? "other" : kind, from: p.page_from, to: p.page_to })
  }
  if (!parts.some((p) => p.kind !== "other")) return [{ kind: docKind(summary, filename) }]
  // A single-kind file is not "partial": no page range to show.
  return parts.every((p) => p.kind === parts[0].kind) ? [{ kind: parts[0].kind }] : parts
}

/** "p.1" or "pp.2-10" for a part of a combined file. */
export function pagesLabel(part: DocPart): string {
  if (part.from === undefined || part.to === undefined) return ""
  return part.from === part.to ? `p.${part.from}` : `pp.${part.from}-${part.to}`
}

/** A Matter file's kind (Nira's vocabulary) as the start page's kind, for what the order still lacks. */
export function fromCatalogKind(kind: string, sim: SimKind): DocKind {
  switch (kind) {
    case "notice": return "scn"
    case "reply": return "reply"
    case "hearing": return "hearing"
    // An order held in the Matter is the one under appeal; an OIO draft has no use for it.
    case "order": return sim === "oia" ? "oio" : "other"
    default: return "other"
  }
}

export const CHIP: Record<DocKind, string> = {
  scn: "SCN", drc: "DRC", reply: "REPLY", hearing: "PH", oio: "OIO", oia: "OIA", apl: "APL", other: "DOC",
}

/** The detected type in words, as it bears on the order chosen. */
export function kindLabel(kind: DocKind, sim: SimKind): string {
  switch (kind) {
    case "scn": return sim === "oia" ? "The notice behind the order" : "Show cause notice"
    case "drc": return "DRC-01 summary"
    case "reply": return "Reply to the notice"
    case "hearing": return "Hearing record"
    case "oio": return sim === "oia" ? "Order under appeal" : "An earlier order"
    case "oia": return "Order-in-Appeal"
    case "apl": return "Appeal and its grounds"
    default: return "Supporting document"
  }
}

export interface Missing { chip: string; text: string; then: string; needed: boolean }

/** What the documents do not include yet, and what Nira does about it. */
export function missing(kinds: DocKind[], sim: SimKind): Missing[] {
  const has = (k: DocKind) => kinds.includes(k)
  const out: Missing[] = []
  if (sim === "oio") {
    if (!has("scn")) out.push({ chip: "SCN", text: "No show cause notice yet", then: "needed to draft", needed: true })
    if (!has("reply")) out.push({ chip: "—", text: "No reply found for this SCN. Add it, or continue without; the order will record that none was filed.", then: "continues without", needed: false })
  } else {
    if (!has("oio")) out.push({ chip: "OIO", text: "No order under appeal yet", then: "needed to draft", needed: true })
    if (!has("apl")) out.push({ chip: "APL", text: "No appeal (APL-01) found", then: "reads the grounds from the order", needed: false })
    if (!has("reply")) out.push({ chip: "—", text: "No reply to the notice found", then: "reads the order's own record of it", needed: false })
  }
  return out
}
